// SheEarnsInsights.jsx
import React, { useState, useMemo } from "react";

// Curated stakeholder groups for SheEarns & SAKSHAM Shakti
// Curated stakeholder groups for SheEarns & SAKSHAM Shakti
const STAKEHOLDERS = [
  {
    id: "selfLearners",
    label: "Self Learners & Women Entrepreneurs",
    emoji: "👩‍💻",
    pains: [
      "Limited access to digital income streams and monetization channels.",
      "Difficulty in accessing curated learning content and workshops.",
      "Challenges in tracking personal growth, productivity, and skills.",
      "Lack of peer recognition and community support.",
      "Limited tools to manage tasks, family, and personal workflow digitally."
    ],
    opportunities: [
      "Digital income tools like ValueShare, Rewarding Ads, and Rewarding Videos.",
      "Interactive learning via Shakti Workshops, Shorts, Podcasts, and Books.",
      "Self-reflection tools including My Memories, Focus Timer, IKIGAI, and AI Counselling.",
      "Community engagement via Shakti Forum, Green Shakti, and Shakti2Farmers."
    ]
  },
  {
    id: "family",
    label: "Family & Household Members",
    emoji: "👨‍👩‍👧‍👦",
    pains: [
      "Unclear value of women’s paid digital activities in household context.",
      "Time constraints when women engage in learning or digital income.",
      "Limited understanding of how digital empowerment affects family welfare."
    ],
    opportunities: [
      "Visibility into women’s achievements and contributions via SheEarns dashboards.",
      "Shared family benefits from additional income earned.",
      "Enhanced household productivity and digital literacy."
    ]
  },
  {
    id: "husbands",
    label: "Husbands & Partners",
    emoji: "💑",
    pains: [
      "Unawareness of income potential from women-led digital initiatives.",
      "Difficulty supporting women in managing multiple roles effectively.",
      "Concerns about ROI and time investment in digital programs."
    ],
    opportunities: [
      "Transparent tracking of earnings and learning progress.",
      "Enhanced family financial security through supplementary digital income.",
      "Engagement in family-oriented learning and task management tools."
    ]
  },
  {
    id: "children",
    label: "Children & Dependents",
    emoji: "👧👦",
    pains: [
      "Indirectly affected by limited family income or mother's time allocation.",
      "Reduced exposure to digital learning and role-modeling.",
      "Lack of awareness of STEM/digital opportunities from early age."
    ],
    opportunities: [
      "Role-model effect through mother’s participation in digital income and learning.",
      "Access to educational content and workshops via SheEarns.",
      "Improved household environment for growth and learning."
    ]
  },
  {
    id: "ngos",
    label: "NGOs & Social Organizations",
    emoji: "🌱",
    pains: [
      "Difficulty in identifying women beneficiaries with real digital engagement.",
      "Challenges in tracking impact and effectiveness of empowerment programs.",
      "Limited ability to provide integrated digital skill-building and income solutions."
    ],
    opportunities: [
      "Partner with Think5Whys SheEarns to monitor outcomes and rewards.",
      "Support scalable women empowerment initiatives.",
      "Engage communities in digital learning, income, and social activities."
    ]
  },
  {
    id: "government",
    label: "Government & Policy Makers",
    emoji: "🏛️",
    pains: [
      "Gap in measurable impact for women’s empowerment schemes.",
      "Challenges integrating digital skill-building and financial inclusion programs.",
      "Limited real-time data to evaluate success of initiatives."
    ],
    opportunities: [
      "SAKSHAM Shakti dashboards provide measurable metrics and KPIs.",
      "Support policy-making with insights from digital income and learning trends.",
      "Scale initiatives with monitored impact and community engagement."
    ]
  },
  {
    id: "corporates",
    label: "Corporate Partners & Sponsors",
    emoji: "🏢",
    pains: [
      "Difficulty identifying credible women-led digital initiatives for partnerships.",
      "Limited tools to track ROI and impact of sponsored programs.",
      "Challenges in CSR implementation for skill-building and income generation."
    ],
    opportunities: [
      "Integrate corporate CSR programs with SheEarns digital platforms.",
      "Track impact, engagement, and recognition of sponsored beneficiaries.",
      "Facilitate collaboration for community, learning, and earning initiatives."
    ]
  },
  {
    id: "selfSellers",
    label: "Digital Sellers & Boutique Owners",
    emoji: "🛍️",
    pains: [
      "Limited access to hyper-local and digital marketplaces.",
      "Difficulty managing inventory and sales digitally.",
      "Low visibility for new products or services.",
      "Challenges in tracking customer engagement and loyalty.",
      "Fragmented payment and reward systems."
    ],
    opportunities: [
      "Shakti Digital Boutique and I Have Product to Sell features for direct sales.",
      "Window Shopping & Digital Tryout to showcase products digitally.",
      "Reward programs like Kharcho Jeeto and Aalu Dalo Sona Nikalo to drive engagement.",
      "Analytics dashboards for tracking customer behavior and payments."
    ]
  }
];


// Key USPs for SheEarns & SAKSHAM Shakti
const USPs = [
  {
    id: "digitalIncome",
    title: "Digital Income Opportunities",
    desc: "Empower women through ValueShare, Rewarding Ads, Rewarding Videos, and Shakti Digital Boutique for direct earnings.",
    emoji: "💰",
  },
  {
    id: "digitalLearning",
    title: "Digital Learning Hub",
    desc: "Access curated workshops, podcasts, books, Shakti Shorts, and interactive learning for continuous skill-building.",
    emoji: "💡",
  },
  {
    id: "selfReflection",
    title: "Self-Reflection Tools",
    desc: "Track personal growth with My Pink Card, Memories, Focus Timer, IKIGAI exercises, AI Counselling, and Journals.",
    emoji: "🪞",
  },
  {
    id: "shaktiShopping",
    title: "Shakti Shopping & Marketplace",
    desc: "Buy, sell, and explore products digitally with Window Shopping, Personal Shopping, Home Basket, and Digital Tryout.",
    emoji: "🛍️",
  },
  {
    id: "communityEngagement",
    title: "Community & Social Connect",
    desc: "Engage with peers, mentors, and farmers via Shakti Forum, Green Shakti, Applause, and BoloGrahak initiatives.",
    emoji: "🤝",
  },
  {
    id: "recognitionProgress",
    title: "Recognition & Progress Tracking",
    desc: "Celebrate achievements and track growth with Shakti Ranking, Innovations, SAKSHAM Shakti Database, and Gamified Challenges.",
    emoji: "🏅",
  },
  {
    id: "analyticsInsights",
    title: "Analytics & Insights",
    desc: "Gain actionable insights on learning, financial impact, and community engagement through dashboards and studies.",
    emoji: "📊",
  },
];

const Pill = ({ children }) => (
  <span className="inline-block bg-pink-700/30 text-pink-100 px-2 py-0.5 rounded-full text-xs font-medium">
    {children}
  </span>
);

const ExpandIcon = ({ open }) => (
  <span className={`ml-2 text-sm ${open ? "rotate-180" : "rotate-0"} transition-transform`}>
    ▼
  </span>
);

export default function SheEarnsInsights() {
  const [tab, setTab] = useState("stakeholders"); 
  const [query, setQuery] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const filteredStakeholders = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return STAKEHOLDERS;
    return STAKEHOLDERS.filter(
      (s) =>
        s.label.toLowerCase().includes(q) ||
        s.pains.some((p) => p.toLowerCase().includes(q)) ||
        s.opportunities.some((o) => o.toLowerCase().includes(q))
    );
  }, [query]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-pink-950 p-4 md:p-8 text-gray-100">
      {/* Header */}
      <header className="mx-auto mb-2 text-center">
        <h1 className="text-lg md:text-xl font-bold text-pink-400">SheEarns & SAKSHAM Shakti Insights</h1>
        <p className="text-gray-300 mt-2 text-sm md:text-base">
          Empowering women through Digital Income, Learning, Self-Reflection, Community, and Recognition.
        </p>
      </header>

      {/* Tabs */}
      <nav className="grid grid-cols-2 md:grid-cols-2 gap-2 justify-center mb-2 place-items-center">
        <button
          onClick={() => setTab("stakeholders")}
          className={`flex items-center justify-center gap-1 px-3 py-2 rounded-md font-semibold text-sm transition-all ${
            tab === "stakeholders"
              ? "bg-pink-600 text-black shadow"
              : "bg-gray-800 text-gray-200 hover:bg-gray-700"
          }`}
        >
          👩‍💻 Stakeholders
        </button>

        <button
          onClick={() => setTab("usps")}
          className={`flex items-center justify-center gap-1 px-3 py-2 rounded-md font-semibold text-sm transition-all ${
            tab === "usps"
              ? "bg-pink-600 text-black shadow"
              : "bg-gray-800 text-gray-200 hover:bg-gray-700"
          }`}
        >
          💡 USPs
        </button>
      </nav>

      {/* Search */}
      <div className="flex flex-col md:flex-row justify-center gap-2 mb-2 place-items-center">
        <input
          aria-label="Search stakeholders"
          placeholder="Search stakeholders, pain or opportunity..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="bg-gray-800 text-gray-200 placeholder-gray-400 px-3 py-2 rounded-md text-sm w-full md:w-80 focus:outline-none focus:ring-2 focus:ring-pink-500"
        />
        <button
          onClick={() => { setQuery(""); setExpandedId(null); }}
          className="text-sm mt-4 w-20 bg-gray-700 px-3 py-2 rounded-md hover:bg-gray-600"
        >
          Reset
        </button>
      </div>

      <main className="max-w-6xl mx-auto space-y-6">
        {/* Stakeholders */}
        {tab === "stakeholders" && (
          <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredStakeholders.map((s) => {
              const isOpen = expandedId === s.id;
              return (
                <article key={s.id} className="bg-gradient-to-br from-gray-800 to-pink-900/30 rounded-lg border border-pink-800/30 shadow-sm overflow-hidden">
                  <div className="p-4 flex items-start gap-3">
                    <div className="text-3xl">{s.emoji}</div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-pink-300">{s.label}</h3>
                        <button
                          onClick={() => setExpandedId(isOpen ? null : s.id)}
                          className="ml-2 bg-pink-700/30 text-sm px-2 py-1 rounded"
                        >
                          {isOpen ? "Collapse" : "Expand"} <ExpandIcon open={isOpen} />
                        </button>
                      </div>
                      <p className="text-sm text-gray-300 mt-2 line-clamp-3">
                        <strong>Pain points:</strong> {s.pains[0]} • {s.pains[1]} • {s.pains[2]}
                      </p>
                      {isOpen && (
                        <div className="mt-3 space-y-3 text-sm text-gray-200">
                          <div>
                            <h4 className="font-medium text-pink-300">Pain Points</h4>
                            <ol className="list-decimal list-inside mt-1 space-y-1">
                              {s.pains.map((p, idx) => <li key={idx}>{p}</li>)}
                            </ol>
                          </div>
                          <div>
                            <h4 className="font-medium text-pink-300">Opportunities</h4>
                            <ul className="list-disc list-inside mt-1 space-y-1">
                              {s.opportunities.map((o, idx) => <li key={idx}>{o}</li>)}
                            </ul>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
            {filteredStakeholders.length === 0 && (
              <div className="bg-gray-800 p-4 rounded text-center text-gray-300">No results found for your search.</div>
            )}
          </section>
        )}

        {/* USPs */}
        {tab === "usps" && (
          <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {USPs.map((u) => (
              <div key={u.id} className="bg-gradient-to-br from-pink-700/20 to-gray-800 rounded-lg p-4 border border-pink-600/20">
                <div className="flex items-start gap-3">
                  <div className="text-2xl">{u.emoji}</div>
                  <div>
                    <h3 className="font-semibold text-lg text-pink-300">{u.title}</h3>
                    <p className="text-sm text-gray-200 mt-2">{u.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </section>
        )}
      </main>

      <footer className="max-w-6xl mx-auto mt-8 text-center text-sm text-gray-400">
        SheEarns & SAKSHAM Shakti • Empowering women with Digital Income, Learning, and Recognition 🌸
      </footer>
    </div>
  );
}
